<template>
  <v-container
    class="py-2 layout-container d-flex flex-column ga-md-4"
    :class="{
      'presentation-layout': isFeatureActive(featureNames.DASHBOARD_PRESENTATION),
      'ga-0': isFeatureActive(featureNames.DASHBOARD_PRESENTATION),
      'ga-2': !isFeatureActive(featureNames.DASHBOARD_PRESENTATION),
    }"
  >
    <DashboardMarkero v-if="isPlatformMarkero" />
    <template v-else>
      <Feature :feature-slug="featureNames.COMPANY">
        <Feature :feature-slug="featureNames.DASHBOARD_PRESENTATION">
          <DashboardPresentationHeader :is-master-view="isMasterView" />
          <template v-slot:disabled>
            <DashboardStandardHeader />
          </template>
        </Feature>
      </Feature>

      <Permission permission="settings-subscription:read">
        <DunningAlert v-if="!isMasterView" />
      </Permission>

      <DeletionAlert v-if="!isMasterView" />

      <Permission permission="settings-subscription:read">
        <CanceledSubscriptionAlert v-if="!isMasterView" />
      </Permission>

      <Feature :feature-slug="featureNames.CRM">
        <ActivationReminder v-if="!isMasterView" />
      </Feature>

      <Feature :feature-slug="featureNames.SIGNUP">
        <UserMailVerification v-if="!isMasterView" />
      </Feature>

      <EmailVerification
        v-if="showEmailVerification && !isMasterView"
        :default-sender="defaultSenderResults?.defaultSender"
      />

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.PRODUCTFRUITS"
      >
        <ProductFruitsChecklist v-if="feature.config.checklistCodeFirstSteps && !isMasterView" />
      </Feature>

      <Feature :feature-slug="featureNames.DASHBOARD_PRESENTATION">
        <DashboardPresentation
          :leads="leadsResults?.leads"
          :statistics="statisticsResults?.statistics"
          :loading="loading"
        />
        <template v-slot:disabled>
          <DashboardStandard
            :default-sender="defaultSenderResults?.defaultSender"
            :leads="leadsResults?.leads"
            :statistics="statisticsResults?.statistics"
            :loading="loading"
          />
        </template>
      </Feature>
    </template>
  </v-container>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import {
  computed,
  defineAsyncComponent,
  getCurrentInstance,
  inject,
  provide,
  readonly,
  ref,
  watch,
} from 'vue'
import { useRoute } from 'vue-router'

import { AuthInstance } from '@/auth'
import Permission from '@/components/Permission'
import { useBranding } from '@/composables/useBranding'
import featureNames from '@/lib/featureNames'
import featureMixin from '@/mixins/feature'
import UserMailVerification from '@/modules/auth/UserMailVerification.vue'
import ActivationReminder from '@/modules/crm/components/ActivationReminder.vue'
import CanceledSubscriptionAlert from '@/modules/dashboard/CanceledSubscriptionAlert.vue'
import { DashboardContextSymbol } from '@/modules/dashboard/dashboardContext'
import DashboardMarkero from '@/modules/dashboard/dashboardMarkero/index.vue'
import DashboardPresentation from '@/modules/dashboard/DashboardPresentation.vue'
import DashboardPresentationHeader from '@/modules/dashboard/DashboardPresentationHeader.vue'
import DashboardStandard from '@/modules/dashboard/DashboardStandard.vue'
import DashboardStandardHeader from '@/modules/dashboard/DashboardStandardHeader.vue'
import DeletionAlert from '@/modules/dashboard/DeletionAlert.vue'
import DunningAlert from '@/modules/dashboard/DunningAlert.vue'
import HOPPERMATION_DEFAULT_SENDER from '@/modules/emailVerification/queries/HoppermationDefaultSender.gql'
import HOPPERMATION_ORGANIZATION from '@/modules/emailVerification/queries/HoppermationOrganization.gql'
import LEADS from '@/modules/map/LeadsWithLocation.gql'
import ProductFruitsChecklist from '@/modules/productFruitsChecklist/index.vue'
import STATISTICS from '@/modules/statistics/Statistics.gql'
import { Feature } from '@/plugins/feature-config'

const EmailVerification = defineAsyncComponent(
  () => import('@/modules/emailVerification/index.vue')
)
// TODO: Set types for role
const role = inject<any>('role')

const instance = getCurrentInstance()
// TODO: Set types for auth
const $auth = inject<AuthInstance>('auth')
const { isPlatformMarkero } = useBranding()
const getFeature = featureMixin.methods.getFeature.bind(instance.proxy)
const isFeatureActive = featureMixin.methods.isFeatureActive.bind(instance.proxy)
const route = useRoute()
const routeParamId = computed(() => route?.params?.id || null)
const isMasterView = computed(() => route.path.startsWith('/master'))

const dateFilters = ref<Date[]>([])

const setDateFilters = (filters: Date[]) => (dateFilters.value = filters)

const filtersInput = computed(() => {
  if (dateFilters.value.length) {
    return {
      filters: {
        startDate: dateFilters.value[0].toISOString(),
        endDate: dateFilters.value[1].toISOString(),
      },
    }
  }
  return {}
})

const hasFiltersApplied = computed(() => !!dateFilters.value.length)

provide(DashboardContextSymbol, {
  dateFilters: readonly(dateFilters),
  setDateFilters,
  formattedDateFilters: filtersInput,
  hasFiltersApplied: hasFiltersApplied,
})

const leadsVariables = computed(() => ({
  input: {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value ?? null,
  },
}))
const { result: leadsResults, refetch: refetchLeadData } = useQuery(LEADS, leadsVariables, {
  enabled: role.isAuthorizedFor('lead:read'),
})

const { result: defaultSenderResults } = useQuery(
  HOPPERMATION_DEFAULT_SENDER,
  {
    companyId: $auth.user.companyId,
  },
  {
    enabled: role.isAuthorizedFor('settings-email:read') && !isMasterView.value,
  }
)

const { result: organizationResults } = useQuery(
  HOPPERMATION_ORGANIZATION,
  {
    companyId: $auth.user.companyId,
  },
  {
    enabled: computed(() => !isMasterView.value),
  }
)

const variables = computed(() => ({
  input: {
    companyId: $auth.user.companyId,
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
    ...filtersInput.value,
  },
}))

const {
  result: statisticsResults,
  loading,
  refetch: refetchStatisticsData,
} = useQuery(STATISTICS, variables)

const showEmailVerification = computed(() => {
  const hoppermationFeature = getFeature(featureNames.HOPPERMATION)
  return (
    !loading.value &&
    hoppermationFeature.isActive &&
    role.isAuthorizedFor('settings-email:read') &&
    !hoppermationFeature.config?.hasStandardSender &&
    (organizationResults?.value?.organization?.status !== 'ACTIVE' ||
      !defaultSenderResults?.value?.isAcknowledged)
  )
})

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      await refetchStatisticsData({
        input: {
          companyId: $auth.user.companyId,
          isMasterView: isMasterView.value,
          childCompanyId: newId,
        },
      })
      await refetchLeadData({ input: { isMasterView: isMasterView.value, childCompanyId: newId } })
    }
  }
)
</script>

<style>
.map-loader .v-skeleton-loader__image {
  height: 400px !important;
  border: none;
}

.toggle-btn {
  position: relative;
  left: 95%;
  top: 32px;
  z-index: 10;
}
@media (max-width: 1025px) {
  .toggle-btn {
    left: 90%;
  }
}

@media (min-width: 1265px) {
  .chart-wrapper {
    max-height: 80px !important;
    position: relative;
    bottom: 20px;
  }
}

@media (max-width: 960px) {
  .presentation-layout {
    background-color: #ffffff;
    .v-card {
      margin: 0;
      border: none;
    }
  }
}
</style>
